import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { BrowserQRCodeReader } from "@zxing/browser";
import {
  FaCamera,
  FaStop,
  FaUpload,
  FaKeyboard,
  FaSpinner,
  FaQrcode,
} from "react-icons/fa";

const CampCheckInScanner = ({ onScan, processing = false }) => {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [scannerError, setScannerError] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [manualPayload, setManualPayload] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadDevices = async () => {
      try {
        const videoDevices = await BrowserQRCodeReader.listVideoInputDevices();
        if (cancelled) {
          return;
        }

        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId((current) => current || videoDevices[0].deviceId);
        }
      } catch (error) {
        if (!cancelled) {
          setScannerError(
            "Camera devices could not be detected on this browser.",
          );
        }
      }
    };

    loadDevices();

    return () => {
      cancelled = true;
      if (controlsRef.current) {
        controlsRef.current.stop();
      }
    };
  }, []);

  const stopScanner = () => {
    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }
    setCameraActive(false);
  };

  const startScanner = async () => {
    setScannerError("");

    try {
      if (!readerRef.current) {
        readerRef.current = new BrowserQRCodeReader();
      }

      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }

      const controls = await readerRef.current.decodeFromVideoDevice(
        selectedDeviceId || undefined,
        videoRef.current,
        (result) => {
          if (!result || processing) {
            return;
          }

          stopScanner();
          onScan(result.getText());
        },
      );

      controlsRef.current = controls;
      setCameraActive(true);
    } catch (error) {
      setScannerError(
        error?.message ||
          "Unable to start the camera scanner. Check camera permissions and try again.",
      );
      setCameraActive(false);
    }
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();

    if (!manualPayload.trim() || processing) {
      return;
    }

    await onScan(manualPayload.trim());
    setManualPayload("");
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || processing) {
      return;
    }

    setUploading(true);
    setScannerError("");
    stopScanner();
    let imageUrl = "";

    try {
      if (!readerRef.current) {
        readerRef.current = new BrowserQRCodeReader();
      }

      imageUrl = URL.createObjectURL(file);
      const result = await readerRef.current.decodeFromImageUrl(imageUrl);
      await onScan(result.getText());
    } catch (error) {
      setScannerError(
        "No QR code was detected in that image. Try a clearer image or use the live camera.",
      );
    } finally {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <ScannerShell>
      <ScannerHeader>
        <div>
          <h3>
            <FaQrcode /> Live Check-In Scanner
          </h3>
          <p>
            Use the camera, upload a QR screenshot, or accept raw text from a
            USB scanner.
          </p>
        </div>
        <ScannerActions>
          {cameraActive ? (
            <ScannerButton
              type="button"
              $variant="danger"
              onClick={stopScanner}
              disabled={processing}
            >
              <FaStop /> Stop Camera
            </ScannerButton>
          ) : (
            <ScannerButton
              type="button"
              onClick={startScanner}
              disabled={processing}
            >
              <FaCamera /> Start Camera
            </ScannerButton>
          )}
          <ScannerButton
            type="button"
            $variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={processing || uploading}
          >
            {uploading ? <FaSpinner className="spin" /> : <FaUpload />}
            Upload QR Image
          </ScannerButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
        </ScannerActions>
      </ScannerHeader>

      {devices.length > 0 && (
        <DeviceRow>
          <label htmlFor="camera-device">Camera</label>
          <select
            id="camera-device"
            value={selectedDeviceId}
            onChange={(event) => setSelectedDeviceId(event.target.value)}
            disabled={cameraActive || processing}
          >
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || "Camera"}
              </option>
            ))}
          </select>
        </DeviceRow>
      )}

      <VideoFrame>
        <video ref={videoRef} muted playsInline />
        {!cameraActive && (
          <VideoPlaceholder>
            <FaCamera />
            <span>Start the camera to scan a camper QR code.</span>
          </VideoPlaceholder>
        )}
      </VideoFrame>

      {scannerError && <ScannerError>{scannerError}</ScannerError>}

      <ManualForm onSubmit={handleManualSubmit}>
        <label htmlFor="manual-qr">
          <FaKeyboard /> Scanner Text Input
        </label>
        <textarea
          id="manual-qr"
          value={manualPayload}
          onChange={(event) => setManualPayload(event.target.value)}
          placeholder="Paste the QR payload here if you are using a hardware scanner or copied scanner text."
          disabled={processing}
        />
        <ScannerButton
          type="submit"
          disabled={processing || !manualPayload.trim()}
        >
          {processing ? <FaSpinner className="spin" /> : <FaQrcode />}
          Check In Camper
        </ScannerButton>
      </ManualForm>
    </ScannerShell>
  );
};

const ScannerShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ScannerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  h3 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin: 0 0 0.4rem;
    color: #0f172a;
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.9rem;
    max-width: 520px;
  }
`;

const ScannerActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ScannerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  font-weight: 700;
  cursor: pointer;
  color: white;
  background: ${(props) => {
    if (props.$variant === "secondary") return "#0f172a";
    if (props.$variant === "danger") return "#dc2626";
    return props.theme.colors.primary;
  }};
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const DeviceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  label {
    font-size: 0.85rem;
    font-weight: 700;
    color: #334155;
  }

  select {
    min-width: 260px;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 0.75rem 0.9rem;
    color: #0f172a;
    background: white;
  }
`;

const VideoFrame = styled.div`
  position: relative;
  min-height: 280px;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid rgba(148, 163, 184, 0.24);

  video {
    width: 100%;
    height: 100%;
    min-height: 280px;
    object-fit: cover;
    display: block;
  }
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  color: rgba(255, 255, 255, 0.82);
  padding: 2rem;
  text-align: center;

  svg {
    font-size: 2rem;
  }
`;

const ScannerError = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  font-weight: 500;
`;

const ManualForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.92rem;
    font-weight: 700;
    color: #334155;
  }

  textarea {
    min-height: 110px;
    border: 1px solid #cbd5e1;
    border-radius: 14px;
    padding: 0.95rem 1rem;
    resize: vertical;
    font-size: 0.92rem;
    line-height: 1.5;
    color: #0f172a;
  }

  textarea:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}22;
  }
`;

export default CampCheckInScanner;
